import CTNCheckbox from '@ctn/ui/components/ctn-checkbox';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNTextArea from '@ctn/ui/components/ctn-textarea';
import CTNUpload from '@ctn/ui/components/ctn-upload';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import { type CTNUploadRef, type FileUpload } from '@ctn/ui/types/ctn-upload';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';

import { useGlobalContext } from '@/context/global.context';
import useApiData from '@/hooks/useApiData';
import {
	getDetailSubsanaciones,
	postSubsanacionesErroresDocumento,
	postSubsanacionesSinErrores,
} from '@/services/tramite.service';

import { TIPO_SUBSANACION } from '@/constants/tramite.constants';
import { formatDoc } from '@/helpers/formSolicitudes';
import { notificationsConfig } from '@/helpers/notifications';
import { commonUpload, getConfigUpload, getIconDocumento } from '@/helpers/upload';
import { peekValidation } from '@/helpers/validations';
import { type ISolicitudDocumentacion } from '@/interfaces/model/don.interface';

import { CONTENT_CONFIG } from './config';
import { defaultValues, getSchema, schema, type Form } from './form';

type ModalSubsanacionDocumentosProps = {
	readonly data: {
		idTramite: ISolicitudDocumentacion['tramite']['id'];
		idTipo: ISolicitudDocumentacion['tipo']['id'];
		id: ISolicitudDocumentacion['id'];
	};
	readonly onClose: () => void;
	readonly onSubmit: () => void;
	readonly isOpen: boolean;
	readonly hasErrorDoc?: boolean;
	readonly idTipoDocumento: number;
};

/**
 * Modal que permite subsanar documentos erroneos o
 * añadir documentos adicionales a la subsanación
 */
const ModalSubsanacionDocumentos = ({
	data: dataSubsanacion,
	hasErrorDoc,
	idTipoDocumento,
	onClose,
	isOpen,
	onSubmit: callbackSubmit,
}: ModalSubsanacionDocumentosProps) => {
	const { openNotification, closeNotification } = useNotification();
	const {
		user,
		maestrosTramite: { tipoDocumento, configuracionTramite },
	} = useGlobalContext();
	const { loading, triggerFetch } = useApiData({ loading: ['detail', 'subsanar'] as const });
	const notificationTemplate = notificationsConfig({ closeNotification });
	const [hasMissingRequired, setHasMissingRequired] = useState<boolean>(true);

	const {
		control,
		reset,
		setValue,
		trigger,
		handleSubmit,
		clearErrors,
		watch,
		formState: { errors, dirtyFields },
	} = useForm<Form>({ defaultValues, resolver: getSchema() as Resolver<Form>, mode: 'onChange' });
	const [notAvaliableDoc] = watch('notAvaliableDoc') ?? [];
	const isAvaliableDoc = notAvaliableDoc !== 'true';
	const refUpload = useRef<CTNUploadRef>(null);
	const [isDocumentoNuevo, setIsDocumentoNuevo] = useState(false);
	const content = hasErrorDoc ? CONTENT_CONFIG.subsanar : CONTENT_CONFIG.adicional;
	const observacionesEmisor = watch('observacionesEmisor');
	const [extsDelDocumento, setExtsDelDocumento] = useState<string[]>([]);

	const formData = watch();

	const documentoErroneo = watch('documentoErroneo');
	const documento = watch('documento');

	const mapData = (data: ISolicitudDocumentacion): Form => {
		const {
			tramite,
			id,
			tipo,
			fechaCreacion,
			observacionesPeticion,
			documentoErroneo,
			tipoDocumento: tipoDocumentoData,			
		} = data;
		setIsDocumentoNuevo(tipo?.id === TIPO_SUBSANACION.NUEVO_DOCUMENTO);

		const dataDocumento = hasErrorDoc ? documentoErroneo?.tipoDocumento : tipoDocumentoData;
		const exts =
			tipoDocumento
				.find((document) => document?.id === dataDocumento?.id)
				?.extensionesPermitidas?.map(({ extension }) => extension) ?? [];
		setExtsDelDocumento(exts);

		return {
			...defaultValues,
			nifEmisor: tramite?.nifEmisor,
			idError: id,
			idTramite: tramite?.id,
			idTipoError: tipo?.id,
			numeroTramite: tramite.numeroTramite,
			fechaCreacion,
			// @ts-expect-error pass
			tipoDocumento: dataDocumento?.descripcion,
			observacionesPeticion,
			documentoErroneo,
		};
	};

	const postSubsanacion = async ({
		nifEmisor,
		idError,
		idTramite,
		idTipoError,
		documento,
		notAvaliableDoc,
		observacionesEmisor,
	}: Form) => {
		if (!user) return;
		const payloadCommon = {
			observacionesEmisor,
			nifEmisor,
			idError,
			idTramite,
			idTipoError,
			tipoSector: user?.tipoSectorEmisor,
		};
		const isAvaliableDoc = notAvaliableDoc[0] !== 'true';
		const fetch = () =>
			isAvaliableDoc && documento
				? postSubsanacionesErroresDocumento({ ...payloadCommon, documento })
				: postSubsanacionesSinErrores(payloadCommon);
		const { code } = await triggerFetch({
			fetch,
			name: 'subsanar',
			error: hasErrorDoc ? (
				<>
					<p>Se ha producido un error en la subsanación del documento.</p>
					<p>Por favor, vuelva a intentarlo</p>
				</>
			) : (
				<>
					<p>Se ha producido un error en el envío del documento.</p>
					<p>Por favor, vuelva a intentarlo</p>
				</>
			),
			onCloseError: () => onClose(),
		});

		if (code !== 200) onClose();

		return code === 200;
	};

	const getData = async () => {
		if (!user) return;
		const { data } = await triggerFetch({
			fetch: getDetailSubsanaciones,
			payload: {
				nifEmisor: user?.nifEmisor,
				idTramite: dataSubsanacion.idTramite,
				idTipoSector: user?.tipoSectorEmisor.id,
				idTipoSubsanacion: dataSubsanacion.idTipo,
				idSubsanacion: dataSubsanacion.id,
			},
			name: 'detail',
		});

		if (data) {
			reset(mapData(data));
		}
	};

	const onSelectFile = (file: FileUpload) => {
		setValue('documento', formatDoc({ file, tipoDocumentoMaestro: tipoDocumento, idTipoDocumento }), {
			shouldDirty: true,
			shouldValidate: true,
			shouldTouch: true,
		});
	};

	const onRemoveFile = () => {
		setValue('documento', undefined, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
	};

	const onSubmit = async (data: Form) => {
		const { tipoDocumento, ...dataToSend } = data;
		// @ts-expect-error pass
		const isSended = await postSubsanacion(dataToSend);
		if (isSended) {
			openNotification(
				notificationTemplate.successMessage(
					hasErrorDoc ? 'Documento subsanado' : 'Documento enviado',
					hasErrorDoc
						? 'El documento ha sido subsanado correctamente.'
						: 'El documento ha sido enviado correctamente.',
				),
			);
			callbackSubmit();
			onClose();
		}
	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cancelar',
			size: 'size-40',
			minWidth: '140',
			onClick: onClose,
		},
		{
			key: 'primary',
			mode: 'primary',
			label: content.submitLabel,
			disabled: loading.detail || hasMissingRequired,
			loading: loading.subsanar,
			minWidth: '140',
			size: 'size-40',
			onClick(e) {
				handleSubmit(onSubmit)(e);
			},
		},
	];

	useEffect(() => {
		if (isOpen) {
			/* Como getData en async limpiamos los datos antes de añadirlos al form,
			 * sino se ven los errores y datos de antes.
			 */
			reset(defaultValues);

			getData();
		}
	}, [isOpen]);

	useEffect(() => {
		const check = async () => {
			const hasAllRequiredFields = await peekValidation({
				schema,
				data: formData,
				fields: Object.keys(formData) as Array<keyof Form>,
			});

			setHasMissingRequired(!hasAllRequiredFields);
		};

		check();
	}, [formData, schema]);

	return (
		<CTNModal
			autoHeight
			closeable
			type="basic"
			className={classNames('c-pnp-modal')}
			title={content.titleModal}
			size="xxl"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<form>
					<div className="[ o-grid ] [ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--radius c-pnp-form-group--display-only ] [ u-flow ]">
						<div className="[ o-grid__row ]">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
								name="fechaCreacion"
								label="Fecha alta"
								control={control}
							/>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
								name="numeroTramite"
								label="Núm solicitud"
								control={control}
							/>
						</div>
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
								name="tipoDocumento"
								label="Tipo"
								control={control}
							/>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="[ o-grid__col-12 o-grid__col-8@portrait ]"
								name="observacionesPeticion"
								label="Motivo de la subsanación"
								control={control}
							/>
						</div>
					</div>
					<div className="[ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--white c-pnp-form-group--radius c-pnp-form-group--display-only ]">
						<CTNCheckbox
							noFeedBack
							checkbox={{ large: true }}
							control={control}
							items={[
								{
									disabled: Boolean(documento),
									label: isDocumentoNuevo
										? 'No disponemos de esta documentación'
										: 'No es necesario realizar cambios en el documento',
									value: 'true',
									icon: {
										icon: 'help',
										color: 'blue-50',
										tooltip: {
											place: 'left',
											maxWidth: 300,
											content:
												'Al activar el check, se deberá indicar los motivos en la sección de Observaciones',
										},
									},
								},
							]}
							name="notAvaliableDoc"
							onChange={() => {
								refUpload.current?.resetUpload();
								setValue('documento', undefined);
								clearErrors();

								if (dirtyFields.documento) trigger('documento');

								if (dirtyFields.observacionesEmisor) trigger('observacionesEmisor');
							}}
						/>
					</div>
					<div className="u-mt-24">
						{documentoErroneo && (
							<CTNUpload
								{...commonUpload}
								displayOnly
								condensed
								noFeedBack
								maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
								label={content?.documentoErroneoLabel}
								fileLoaded={
									documentoErroneo?.nombre
										? { name: documentoErroneo?.nombre, icon: getIconDocumento(documentoErroneo) }
										: undefined
								}
							/>
						)}
						<CTNUpload
							{...commonUpload}
							{...getConfigUpload({
								idTipoDocumento: Number(idTipoDocumento ?? 0),
								tipoDocumentoMaestros: tipoDocumento,
								value: documento,
							})}
							ref={refUpload}
							maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
							validExtensions={extsDelDocumento}
							classNameField="u-mt-24"
							required={isAvaliableDoc}
							disabled={!isAvaliableDoc}
							label={content?.documenoLabel}
							error={errors.documento?.message}
							onRemoveFile={() => onRemoveFile()}
							onSelectFile={onSelectFile}
						/>
						<fieldset>
							<legend className="c-pnp-heading-h4">Observaciones sobre la solicitud</legend>
							<CTNTextArea
								classNameField="u-mt-16"
								required={!isAvaliableDoc}
								name="observacionesEmisor"
								label="Observaciones"
								control={control}
								maxLength={2000}
								error={errors.observacionesEmisor?.message}
								placeholder="Indicar aquí las observaciones."
								message={`${observacionesEmisor.length}/2000`}
							/>
						</fieldset>
					</div>
				</form>
			}
			onClose={onClose}
		/>
	);
};

export default ModalSubsanacionDocumentos;
